import React, { useRef, useState, useCallback, useEffect } from "react";
import PropTypes from 'prop-types';
import { IMaskInput } from 'react-imask';

function parseMaskValue(value = '') {
  // Pad the string to ensure indices are safe to check
  const paddedValue = (value || '').padEnd(10, ' ');

  return {
    feet: /[0-9]/.test(paddedValue[0]) ? paddedValue[0] : null,
    inchTens: /[0-9]/.test(paddedValue[5]) ? paddedValue[5] : null,
    inchUnits: /[0-9]/.test(paddedValue[6]) ? paddedValue[6] : null,
  };
}

function buildMaskValue({ feet, inchTens, inchUnits }) {
  // Return empty string if fully clear so floating labels collapse properly
  if (feet === null && inchTens === null && inchUnits === null) return '';

  const f = feet ?? ' ';
  const it = inchTens ?? ' ';
  const iu = inchUnits ?? ' ';
  return `${f} ft ${it}${iu} in`;
}

const InputMaskHeight = React.forwardRef(function InputMaskHeight(props, ref) {
  const { inputRef, onChange, onFocus, onBlur, onKeyDown, name, value: propValue, ...inputMaskProps } = props;

  const [focused, setFocused] = useState(false);
  const [hasValue, setHasValue] = useState(() => (propValue || '').replace(/[^0-9]/g, '').length > 0);

  const stateRef = useRef(parseMaskValue(propValue));
  const prevPropRef = useRef(propValue);

  // Sync internal state if parent prop changes externally
  if (propValue !== prevPropRef.current) {
    stateRef.current = parseMaskValue(propValue);
    prevPropRef.current = propValue;
  }

  const displayValue = buildMaskValue(stateRef.current);

  const emit = useCallback((nextState) => {
    // Strictly enforce two-digit padding. Inches are either both null, or both filled.
    if (nextState.inchUnits !== null && nextState.inchTens === null) {
      nextState.inchTens = '0';
    } else if (nextState.inchUnits === null && nextState.inchTens !== null) {
      nextState.inchUnits = '0';
    }

    stateRef.current = nextState;
    const nextString = buildMaskValue(nextState);
    prevPropRef.current = nextString;

    if (onChange) {
      onChange({ target: { name, value: nextString } });
    }
  }, [onChange, name]);

  const handleOnAccept = useCallback((value) => {
    const nextHasValue = value.replace(/[^0-9]/g, '').length > 0;

    // Avoid a mask-level re-render when the value/non-value state has not changed.
    setHasValue((previousHasValue) => (previousHasValue === nextHasValue ? previousHasValue : nextHasValue));

    // RECURSION GUARD: Only fire onChange if IMask internally changes the value 
    if (value !== prevPropRef.current && value !== propValue) {
      if (onChange) onChange({ target: { name, value } });
    }
  }, [onChange, propValue, name]);

  const handleKeyDown = useCallback((e) => {
    const isDigit = e.key >= '0' && e.key <= '9';
    const isBackspace = e.key === 'Backspace';
    const isDelete = e.key === 'Delete';

    // If it's a regular key (Tab, arrows, letters), let the browser handle it naturally
    if (!isDigit && !isBackspace && !isDelete) {
      if (onKeyDown) onKeyDown(e);
      return;
    }

    e.preventDefault();
    const s = { ...stateRef.current };
    const { selectionStart: start, selectionEnd: end } = e.target;

    const setCursor = (pos) => setTimeout(() => e.target?.setSelectionRange(pos, pos), 0);

    // Handle highlighted text deletion
    if (start !== end) {
      if (start <= 0 && end > 0) s.feet = null;
      if (start <= 5 && end > 5) { s.inchTens = null; s.inchUnits = null; }
      if (start <= 6 && end > 6) { s.inchTens = null; s.inchUnits = null; }

      // If the user just pressed delete/backspace on highlighted text, stop here.
      if (!isDigit) {
        setCursor(start);
        emit(s);
        if (onKeyDown) onKeyDown(e);
        return;
      }
    }

    // Handle Text Insertion (Typing digits)
    if (isDigit) {
      if (start <= 0) {
        s.feet = e.key;
        if (s.inchTens === null && s.inchUnits === null) {
          s.inchTens = '0';
          s.inchUnits = '0';
        }
        setCursor(5);
      } else {
        // ATM-style Shift Left for inches
        s.inchTens = s.inchUnits || '0';
        s.inchUnits = e.key;
        setCursor(7);
      }
    }
    // Handle Backspace (Left Delete)
    else if (isBackspace) {
      if (start >= 7) {
        if (s.inchTens === '0' && s.inchUnits === '0') {
          s.inchTens = null; s.inchUnits = null; setCursor(5);
        } else if (s.inchTens !== null || s.inchUnits !== null) {
          s.inchUnits = s.inchTens || '0'; s.inchTens = '0'; setCursor(7);
        } else {
          setCursor(5);
        }
      } else if (start >= 6) {
        s.inchTens = '0'; setCursor(5);
      } else if (start >= 1 && start <= 5) {
        s.feet = null; setCursor(0);
      }
    }
    // Handle Delete (Right Delete)
    else if (isDelete) {
      if (start <= 0) { s.feet = null; setCursor(0); }
      else if (start <= 5) { s.inchTens = '0'; setCursor(5); }
      else if (start <= 6) { s.inchUnits = '0'; setCursor(6); }
    }

    emit(s);
    if (onKeyDown) onKeyDown(e);
  }, [emit, onKeyDown]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text/plain');
    if (!pastedText) return;

    const { selectionStart: start, selectionEnd: end } = e.target;

    // Simulate paste into the baseline string to properly extract digits
    const currentString = displayValue || '  ft    in';
    const newString = currentString.substring(0, start) + pastedText + currentString.substring(end);
    const digits = newString.replace(/[^0-9]/g, '');

    // Ignore paste if it contained no numbers
    if (digits.length === 0) return;

    const s = { feet: null, inchTens: null, inchUnits: null };
    s.feet = digits[0];

    if (digits.length === 2) {
      s.inchTens = '0';
      s.inchUnits = digits[1];
    } else if (digits.length >= 3) {
      s.inchTens = String(Math.min(parseInt(digits[1], 10), 9));
      s.inchUnits = digits[2];
    }

    emit(s);

    // Set cursor cleanly based on how many numbers were successfully pasted
    setTimeout(() => {
      if (e.target) {
        const cursorPos = digits.length === 1 ? 5 : 7;
        e.target.setSelectionRange(cursorPos, cursorPos);
      }
    }, 0);
  }, [displayValue, emit]);

  const handleFocus = useCallback((e) => {
    setFocused(true);
    if (e.target) setTimeout(() => e.target.setSelectionRange(0, 0), 0);
    if (onFocus) onFocus(e);
  }, [onFocus]);

  const handleBlur = useCallback((e) => {
    setFocused(false);
    if (onBlur) onBlur(e);
  }, [onBlur]);

  return (
    <IMaskInput
      {...inputMaskProps}
      inputRef={ref || inputRef}
      mask="0 {ft} 00 {in}"
      lazy={!focused && !hasValue}
      placeholderChar=" "
      value={displayValue}
      onAccept={handleOnAccept}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onFocus={handleFocus}
      onBlur={handleBlur}
      overwrite={false}
    />
  );
});

InputMaskHeight.propTypes = {
  inputRef: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  name: PropTypes.string,
  value: PropTypes.string,
};

export default InputMaskHeight;
